// src/components/weopen/WeOpenContactContext.tsx
//
// CRM contact context for a WeOpen conversation (Phase 3 PR 3).
//
// ── THE ONE LINK ────────────────────────────────────────────────────────────
//
// `WeOpenConversationDetail.crmContactId` → `Contact.id`. That is the entire
// linking rule. WeOpen decides which conversation an agent may see; CRM decides
// which contact an agent may see; this component does nothing but join the two
// by an id WeOpen already told us.
//
// ── NO IDENTITY GUESSING, EVER ──────────────────────────────────────────────
//
// When `crmContactId` is null there is NO contact, and this component makes no
// request at all. It does not search by email, phone or `externalUserId`, and it
// does not create, find-or-create, repair or attach anything. Guessing would
// invent a link WeOpen never asserted, and a wrong guess here means showing one
// customer's record while talking to a different person. An absent link is
// information, not a problem to solve.
//
// ── TWO AUTHORITIES, BOTH ENFORCED ──────────────────────────────────────────
//
// Seeing a conversation does NOT authorize reading a contact. This calls CRM's
// existing `GET /api/contacts/:id`, which independently applies
// `contacts:read` capability, `checkWorkspaceAccess` (403 across workspaces) and
// the restricted-record check. There is no bypass here and no direct table
// access: if CRM refuses, nothing renders.
//
// Note on 404 — CRM answers 404 BOTH for "no such contact" and for a contact the
// principal is not allowed to see (`assertContactVisible` deliberately hides the
// difference so a restricted record is indistinguishable from a missing one). So
// this component must never say "this contact does not exist"; it says
// unavailable, which is the only thing the status actually supports.
//
// ── CONTACT FAILURE MUST NOT COST THE CONVERSATION ──────────────────────────
//
// Every failure state is local to this box. The conversation, its status and its
// messages stay on screen, because a CRM contact hiccup is not a reason to hide
// a customer who is waiting for a reply.
//
// ── READ-ONLY ───────────────────────────────────────────────────────────────
//
// No contact mutation and no conversation mutation. Navigation goes to CRM's
// existing contact deep link; no new contact route is introduced.

import { Link } from 'react-router-dom';
import { useGetContactQuery } from '../../api/contactsApi';

/**
 * CRM's existing contact deep link. `/phone/contact/:crmContactId` renders
 * `ContactWorkspacePage`, which opens the contact drawer — it is the only
 * supported contact-detail deep link in the router, and it is already keyed on
 * the CRM contact id. A new `/contacts/:id` route is deliberately NOT added.
 */
export function contactHref(contactId: string): string {
  return `/phone/contact/${encodeURIComponent(contactId)}`;
}

/** The distinguishable outcomes. Named so the render path cannot blur them. */
export type ContactContextState =
  | 'no-link' // crmContactId is null — no request is made
  | 'loading'
  | 'loaded'
  | 'unavailable' // 404: missing OR restricted-and-invisible. Indistinguishable.
  | 'forbidden' // 403: another workspace, or no workspace authority
  | 'error'; // anything else, including 5xx

/** RTK Query surfaces the HTTP status on `error.status` for fetch errors. */
function statusOf(err: unknown): number | undefined {
  if (err && typeof err === 'object' && 'status' in err) {
    const s = (err as { status: unknown }).status;
    if (typeof s === 'number') return s;
  }
  return undefined;
}

export function contactContextState(args: {
  crmContactId: string | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  hasData: boolean;
}): ContactContextState {
  if (!args.crmContactId) return 'no-link';
  if (args.isLoading) return 'loading';
  if (args.isError) {
    const s = statusOf(args.error);
    if (s === 404) return 'unavailable';
    if (s === 403) return 'forbidden';
    return 'error';
  }
  return args.hasData ? 'loaded' : 'unavailable';
}

export interface WeOpenContactContextProps {
  /** From WeOpen conversation DETAIL only. Never from an inbox row. */
  crmContactId: string | null;
}

export default function WeOpenContactContext({ crmContactId }: WeOpenContactContextProps) {
  // `skip` is what guarantees "no link ⇒ no request". Not a guard inside the
  // handler, not a discarded response — the request is never issued.
  const contact = useGetContactQuery(crmContactId as string, { skip: !crmContactId });

  const state = contactContextState({
    crmContactId,
    isLoading: contact.isLoading,
    isError: contact.isError,
    error: contact.error,
    hasData: !!contact.data,
  });

  if (state === 'no-link') return null;

  return (
    <div data-testid="weopen-contact-context" data-state={state} className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">CRM contact</p>

      {state === 'loading' && (
        <div data-testid="weopen-contact-loading" className="mt-2 h-4 w-40 animate-pulse rounded bg-gray-200" />
      )}

      {/* 404 — deliberately NOT "does not exist". CRM returns 404 for a
          restricted contact too, and no repair is attempted. */}
      {state === 'unavailable' && (
        <p data-testid="weopen-contact-unavailable" className="mt-2 text-sm text-gray-500">
          Contact unavailable.
        </p>
      )}

      {/* 403 — say only that access is refused. No fields, not even a name. */}
      {state === 'forbidden' && (
        <p data-testid="weopen-contact-forbidden" className="mt-2 text-sm text-gray-500">
          You do not have access to this contact.
        </p>
      )}

      {state === 'error' && (
        <div data-testid="weopen-contact-error" className="mt-2">
          <p className="text-sm text-gray-500">Contact details could not be loaded.</p>
          <button
            type="button"
            onClick={() => void contact.refetch()}
            className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Retry
          </button>
        </div>
      )}

      {state === 'loaded' && contact.data && (
        <div data-testid="weopen-contact-loaded" className="mt-2 space-y-1">
          <p data-testid="weopen-contact-name" className="text-sm font-semibold text-gray-900">
            {contact.data.fullName}
          </p>
          {contact.data.company && (
            <p data-testid="weopen-contact-company" className="text-xs text-gray-600">
              {contact.data.company}
            </p>
          )}
          {contact.data.email && (
            <p data-testid="weopen-contact-email" className="text-xs text-gray-600">
              {contact.data.email}
            </p>
          )}
          {contact.data.phone && (
            <p data-testid="weopen-contact-phone" className="text-xs text-gray-600">
              {contact.data.phone}
            </p>
          )}
          <Link
            data-testid="weopen-contact-link"
            to={contactHref(contact.data.id)}
            className="mt-1 inline-block text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Open in CRM
          </Link>
        </div>
      )}
    </div>
  );
}
